import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowLeft, Lock, MessageCircle, ShieldCheck, X, Zap } from 'lucide-react';
import { initSocket } from '../../services/socketService';
import { chatApi, CHAT_KANALEN_KEY, CHAT_ONGELEZEN_KEY, type ChatKanaal } from '../../services/veiligeHavenApi';

type Rol = 'medewerker' | 'professional';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

const tijd = (iso: string) => new Date(iso).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Socket-seintje (zonder inhoud) → tellers, lijst en open gesprek verversen. */
function useChatSocket() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: ['veilige-chat'] });
    socket.on('veilige_chat', ververs);
    return () => {
      socket.off('veilige_chat', ververs);
    };
  }, [queryClient]);
}

const kanaalTitel = (k: ChatKanaal, rol: Rol, casus: string) =>
  rol === 'professional' ? `${casus} #${k.casus_nummer}` : k.professional_naam || casus;

/**
 * 💬 Mijn Veilige Gesprekken — zwevende chat-console rechtsonder (onderdeel 60, Sectie 1.2).
 * Rust-state: discreet blauw icoon; badge met het aantal ongelezen berichten.
 * Openklikken schuift een vertrouwelijk venster omhoog: links de geverifieerde
 * koppelingen, rechts de versleutelde tekststroom. De professional ziet alleen
 * het casusnummer.
 */
export const VeiligeGesprekkenNode: React.FC<{ rol: Rol }> = ({ rol }) => {
  const { t } = useTranslation('medewerker');
  const [open, setOpen] = useState(false);
  const [actiefId, setActiefId] = useState<string | null>(null);
  useChatSocket();

  const ongelezen = useQuery({ queryKey: CHAT_ONGELEZEN_KEY, queryFn: chatApi.ongelezen, refetchInterval: 30_000, retry: false });
  const aantal = ongelezen.data?.aantal ?? 0;

  // Eerst mounten, één frame later inschuiven: anders is er geen overgang te zien
  const [ingeschoven, setIngeschoven] = useState(false);
  useEffect(() => {
    if (!open) {
      setIngeschoven(false);
      return;
    }
    const frame = requestAnimationFrame(() => setIngeschoven(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open]);

  // Medewerker: boven de tabbalk. Professional: boven de vaste Feedback-, zoek- en supportknoppen rechtsonder.
  const knopPlek = rol === 'medewerker' ? 'bottom-[calc(5.5rem+env(safe-area-inset-bottom))]' : 'bottom-40';
  const label = rol === 'medewerker' ? t('chat.knop', { defaultValue: 'Mijn Veilige Gesprekken' }) : t('chat.knopProfessional', { defaultValue: 'Veilige gesprekken' });

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`fixed right-4 ${knopPlek} z-30 inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] pl-3.5 pr-4 py-3 text-sm font-medium text-white shadow-lg ring-1 ring-black/5 hover:bg-[#13305a] transition-colors`}
          aria-label={aantal > 0 ? `${label} (${aantal} ${t('chat.ongelezen', { defaultValue: 'ongelezen' })})` : label}
        >
          <MessageCircle className="w-5 h-5" aria-hidden="true" />
          <span className="hidden sm:inline">{label}</span>
          {aantal > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-[11px] font-bold text-white flex items-center justify-center ring-2 ring-white">
              {aantal > 99 ? '99+' : aantal}
            </span>
          )}
        </button>
      )}

      <div
        className={`fixed inset-x-0 bottom-0 sm:inset-x-auto sm:right-4 sm:bottom-4 z-[60] transition-transform duration-300 ease-out motion-reduce:transition-none ${
          ingeschoven ? 'translate-y-0' : 'translate-y-[110%] pointer-events-none'
        }`}
        role="dialog"
        aria-modal="false"
        aria-label={label}
        aria-hidden={!open}
      >
        {open && (
          <div className="w-full sm:w-[680px] h-[78vh] sm:h-[540px] flex flex-col rounded-t-2xl sm:rounded-2xl bg-[#FCFBF8] border border-[#E9E4D8] shadow-2xl overflow-hidden">
            <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#E9E4D8] bg-white">
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{label}</p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {t('chat.versleuteld', { defaultValue: 'Versleuteld opgeslagen · je werkgever kan niet meelezen' })}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label={t('algemeen.sluiten', { defaultValue: 'Sluiten' })}>
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </header>
            <ChatMatrix rol={rol} actiefId={actiefId} setActiefId={setActiefId} />
          </div>
        )}
      </div>
    </>
  );
};

const ChatMatrix: React.FC<{ rol: Rol; actiefId: string | null; setActiefId: (id: string | null) => void }> = ({ rol, actiefId, setActiefId }) => {
  const { t } = useTranslation('medewerker');
  const casus = t('chat.casus', { defaultValue: 'Casus' });
  const kanalen = useQuery({ queryKey: CHAT_KANALEN_KEY, queryFn: chatApi.kanalen, refetchInterval: 30_000 });
  const lijst = useMemo(() => kanalen.data ?? [], [kanalen.data]);

  // Op brede schermen meteen het bovenste gesprek openen
  useEffect(() => {
    if (!actiefId && lijst.length > 0 && window.matchMedia('(min-width: 640px)').matches) setActiefId(lijst[0].id);
  }, [actiefId, lijst, setActiefId]);

  const actief = lijst.find((k) => k.id === actiefId) ?? null;

  return (
    <div className="flex-1 flex min-h-0">
      <nav
        className={`${actief ? 'hidden sm:flex' : 'flex'} w-full sm:w-56 shrink-0 flex-col border-r border-[#E9E4D8] bg-white/60 overflow-y-auto`}
        aria-label={t('chat.lijst', { defaultValue: 'Gesprekken' })}
      >
        {kanalen.isLoading ? (
          <div className="p-4 space-y-2">
            <div className="h-10 rounded-lg bg-gray-100 animate-pulse" />
            <div className="h-10 rounded-lg bg-gray-100 animate-pulse" />
          </div>
        ) : kanalen.isError ? (
          <p className="p-4 text-sm text-red-600" role="alert">{foutMelding(kanalen.error, t('chat.laadfout', { defaultValue: 'Gesprekken konden niet worden geladen.' }))}</p>
        ) : lijst.length === 0 ? (
          <div className="p-5 text-center">
            <Lock className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
            <p className="text-sm text-gray-600">
              {rol === 'medewerker'
                ? t('chat.leeg', { defaultValue: 'Nog geen veilige gesprekken. Zodra een professional je afspraak bevestigt of je spoed-verzoek oppakt, kun je hier vertrouwelijk chatten.' })
                : t('chat.leegProfessional', { defaultValue: 'Nog geen gesprekken. Een kanaal gaat open na een goedgekeurde afspraak of een geclaimd terugbelverzoek.' })}
            </p>
          </div>
        ) : (
          <ul className="p-2 space-y-1">
            {lijst.map((k) => (
              <li key={k.id}>
                <button
                  type="button"
                  onClick={() => setActiefId(k.id)}
                  aria-current={k.id === actiefId ? 'true' : undefined}
                  className={`w-full text-left rounded-lg px-3 py-2 transition-colors ${k.id === actiefId ? 'bg-[#0B1F3A]/[0.07]' : 'hover:bg-gray-50'}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${k.ongelezen ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>{kanaalTitel(k, rol, casus)}</span>
                    {k.ongelezen > 0 && (
                      <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-[11px] font-bold text-white flex items-center justify-center">{k.ongelezen}</span>
                    )}
                  </span>
                  <span className="block text-xs text-gray-500 truncate">
                    {k.gesloten ? t('chat.gesloten', { defaultValue: 'Gesloten' }) : rol === 'medewerker' ? k.discipline_label : k.laatste_bericht_op ? tijd(k.laatste_bericht_op) : t('chat.nieuw', { defaultValue: 'Nieuw kanaal' })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </nav>

      {actief ? (
        <ChatVenster key={actief.id} kanaal={actief} rol={rol} onTerug={() => setActiefId(null)} />
      ) : (
        <div className="hidden sm:flex flex-1 items-center justify-center p-6 text-sm text-gray-400">
          {lijst.length > 0 && t('chat.kies', { defaultValue: 'Kies een gesprek' })}
        </div>
      )}
    </div>
  );
};

const ChatVenster: React.FC<{ kanaal: ChatKanaal; rol: Rol; onTerug: () => void }> = ({ kanaal, rol, onTerug }) => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const [invoer, setInvoer] = useState('');
  const eindRef = useRef<HTMLDivElement>(null);
  const casus = t('chat.casus', { defaultValue: 'Casus' });

  const stroom = useQuery({
    queryKey: ['veilige-chat', 'berichten', kanaal.id],
    queryFn: () => chatApi.berichten(kanaal.id),
    refetchInterval: 8_000,
  });
  const berichten = stroom.data?.berichten ?? [];
  const gesloten = stroom.data?.kanaal.gesloten ?? kanaal.gesloten;

  // Gelezen-markering loopt via het ophalen: tellers daarna verversen
  const aantalBerichten = berichten.length;
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: CHAT_ONGELEZEN_KEY });
    queryClient.invalidateQueries({ queryKey: CHAT_KANALEN_KEY });
    eindRef.current?.scrollIntoView({ block: 'end' });
  }, [aantalBerichten, queryClient]);

  const verstuur = useMutation({
    mutationFn: (tekst: string) => chatApi.verstuur(kanaal.id, tekst),
    onSuccess: () => {
      setInvoer('');
      queryClient.invalidateQueries({ queryKey: ['veilige-chat', 'berichten', kanaal.id] });
    },
  });

  const verzend = () => {
    const tekst = invoer.trim();
    if (!tekst || verstuur.isPending || gesloten) return;
    verstuur.mutate(tekst);
  };

  return (
    <section className="flex-1 flex flex-col min-w-0" aria-label={kanaalTitel(kanaal, rol, casus)}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E9E4D8] bg-white/70">
        <button type="button" onClick={onTerug} className="sm:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" aria-label={t('berichten.terug', { defaultValue: 'Terug' })}>
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {kanaalTitel(kanaal, rol, casus)}
            {rol === 'medewerker' && kanaal.discipline_label ? <span className="font-normal text-gray-500"> — {kanaal.discipline_label}</span> : null}
          </p>
          <p className="text-[11px] text-gray-500 truncate">
            {rol === 'medewerker'
              ? t('chat.jijBent', { defaultValue: 'Je professional ziet jou alleen als {{casus}} #{{nr}}', casus, nr: kanaal.casus_nummer })
              : t('chat.anoniem', { defaultValue: 'Anonieme medewerker — naam en gegevens zijn afgeschermd' })}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5" aria-live="polite">
        {stroom.isLoading && <div className="h-10 w-1/2 rounded-xl bg-gray-100 animate-pulse" />}
        {stroom.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(stroom.error, t('chat.laadfout', { defaultValue: 'Gesprekken konden niet worden geladen.' }))}</p>}
        {berichten.map((m) => (
          <div key={m.id} className={`flex ${m.van === 'ik' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                m.van === 'ik' ? 'bg-[#0B1F3A] text-white rounded-br-md' : 'bg-white border border-[#E9E4D8] text-gray-800 rounded-bl-md'
              }`}
            >
              {m.tekst ?? <em className="opacity-70">{t('chat.onleesbaar', { defaultValue: 'Bericht kon niet worden ontsleuteld' })}</em>}
              <span className={`block mt-1 text-[10px] ${m.van === 'ik' ? 'text-blue-100' : 'text-gray-400'}`}>{tijd(m.created_at)}</span>
            </div>
          </div>
        ))}
        <div ref={eindRef} />
      </div>

      {gesloten ? (
        <p className="m-2 rounded-lg bg-gray-100 px-3 py-2 text-xs text-gray-600 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {t('chat.kanaalGesloten', { defaultValue: 'Dit gesprek is gesloten. Er kunnen geen berichten meer worden verstuurd.' })}
        </p>
      ) : (
        <form
          className="p-2 border-t border-[#E9E4D8] bg-white flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            verzend();
          }}
        >
          <label className="sr-only" htmlFor={`chat-invoer-${kanaal.id}`}>
            {t('chat.invoer', { defaultValue: 'Typ hier uw anonieme bericht...' })}
          </label>
          <input
            id={`chat-invoer-${kanaal.id}`}
            value={invoer}
            onChange={(e) => setInvoer(e.target.value)}
            maxLength={4000}
            autoComplete="off"
            placeholder={rol === 'medewerker' ? t('chat.invoer', { defaultValue: 'Typ hier uw anonieme bericht...' }) : t('chat.invoerProfessional', { defaultValue: 'Typ uw bericht...' })}
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/20"
          />
          <button
            type="submit"
            disabled={!invoer.trim() || verstuur.isPending}
            className="px-3 rounded-lg bg-[#0B1F3A] text-white disabled:opacity-40"
            aria-label={t('berichten.verstuur', { defaultValue: 'Verstuur' })}
          >
            <Zap className="w-5 h-5" aria-hidden="true" />
          </button>
        </form>
      )}
      {verstuur.isError && (
        <p className="px-3 pb-2 text-xs text-red-600" role="alert">
          {foutMelding(verstuur.error, t('chat.verstuurFout', { defaultValue: 'Versturen mislukt. Probeer het opnieuw.' }))}
        </p>
      )}
    </section>
  );
};

export default VeiligeGesprekkenNode;
